import './edit-user-modal.scss';
import { useEffect } from 'react';
import { Trash } from 'react-feather';
import { useForm } from 'react-hook-form';
import {
  useOpenOldModal,
  useModalData,
  useCloseOldModal,
} from '../../../../store/modal/modalHooks';
import { useTranslation } from 'react-i18next';
import 'cleave.js/dist/addons/cleave-phone.us';
import Toast from '../../../shared/Toast/Toast';
import { useProfile } from '../../../../store/Auth/authHooks';
import { Button, Form, ModalFooter, ModalHeader } from 'reactstrap';
import {
  useUserManagementUpdateUser,
  useUserManagementRemoveUser,
  useCompanyManagementGetUserById,
  useCompanyManagementCompanies,
  useCompanyManagementSetCompanyUsers,
} from '../../../../store/CompanyManagement/companyManagementHooks';
import { ModalComponentEnum } from '../../../../store/modal/modalTypes';
import { GeneralInformation } from '../GeneralInformation/GeneralInformation';

const EditUserModal = () => {
  const profile = useProfile();
  const { t } = useTranslation();
  const openModal = useOpenOldModal();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const removeUser = useUserManagementRemoveUser();
  const updateUser = useUserManagementUpdateUser();
  const companies = useCompanyManagementCompanies();
  const fetchCompanyUsers = useCompanyManagementSetCompanyUsers();
  const user = useCompanyManagementGetUserById(modalData?.data?.id);

  useEffect(() => {
    reset({
      fullName: user?.fullName || '',
      email: user?.email || '',
      job_title: user?.jobTitle || '',
      companyAdmin: user?.companyAdmin || false,
      company: profile.companyId,
      user_type: user?.userType || 'resident',
      phone: user?.phone || '',
      facebook: user?.socialMedia?.facebook || '',
      linkedin: user?.socialMedia?.linkedin || '',
      twitter: user?.socialMedia?.twitter || '',
      instagram: user?.socialMedia?.instagram || '',
      city: user?.address?.city || '',
      street: user?.address?.street || '',
      zipCode: user?.address?.zip || '',
      country: user?.address?.country || '',
      status: 'Active',
    });
  }, []);

  const defaultValues = {
    fullName: user?.fullName || '',
    email: user?.email || '',
    job_title: user?.jobTitle || '',
    companyAdmin: user?.companyAdmin || false,
    company: profile.companyId,
    user_type: user?.userType || 'resident',
    phone: user?.phone || '',
    facebook: user?.socialMedia?.facebook || '',
    linkedin: user?.socialMedia?.linkedin || '',
    twitter: user?.socialMedia?.twitter || '',
    instagram: user?.socialMedia?.instagram || '',
    city: user?.address?.city || '',
    street: user?.address?.street || '',
    zipCode: user?.address?.zip || '',
    country: user?.address?.country || '',
    status: 'Active',
  };

  const form = useForm({});
  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = form;

  const onDeleteGuest = () => {
    openModal(ModalComponentEnum.QUESTION_REMOVE_USER_MODAL, {}, {}, () => {
      removeUser(modalData?.data?.id)
        .then(() => {
          Toast('success-request', t('DELETE_USER_SUCCESS_MESSAGE'));
          fetchCompanyUsers(profile.companyId!).finally();
        })
        .catch((error) => {
          Toast('error', error.response.data.error.message);
        })
        .finally(() => closeModal());
    });
  };

  const onSubmit = (data: any) => {
    const company = companies.find((company) => company.id === data.company);
    const newData = {
      ...data,
      locationId: company?.locationId || -1,
      company_token: company?.token,
      company_id: company?.id,
      company_admin: data?.companyAdmin,
      userType: data.user_type,
      socialMedia:
        data.facebook || data.linkedin || data.instagram || data.twitter
          ? {
              facebook: data.facebook,
              linkedin: data.linkedin,
              instagram: data.instagram,
              twitter: data.twitter,
            }
          : undefined,
      address:
        data.city || data.street || data.zipCode || data.country
          ? {
              city: data.city,
              street: data.street,
              zip: data.zipCode,
              country: data.country,
            }
          : undefined,
      status: data.status,
      jobTitle: data.job_title,
    };

    updateUser(modalData?.data?.id, newData)
      .then(() => {
        fetchCompanyUsers(profile.companyId!).finally();
        Toast(
          'success-request',
          t('UPDATE_USER_SUCCESS_MESSAGE', { email: data.email })
        );
      })
      .catch((error) => {
        Toast('error', error.response.data.error.message);
      })
      .finally(() => closeModal());
  };

  return (
    <div className="edit-user-modal">
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <p className="form-header-title">{t('EDIT_USER')}</p>
          </ModalHeader>
          <>
            <GeneralInformation form={form} isEdit />
          </>
          <ModalFooter className="edit-user-buttons">
            <div>
              <Button className="delete-user" color="" onClick={onDeleteGuest}>
                <Trash size={10} />
                {t('DELETE_USER')}
              </Button>
            </div>
            <div>
              <Button type="button" color="" onClick={closeModal}>
                {t('CLOSE_BUTTON')}
              </Button>{' '}
              <Button type="submit" color="primary">
                {t('SAVE_CHANGES')}
              </Button>{' '}
            </div>
          </ModalFooter>
        </Form>
      </div>
    </div>
  );
};

export default EditUserModal;
